import * as Yup from 'yup';

import { format, isBefore, isAfter } from 'date-fns';
import { getValue, paymentMethod, paymentType } from '@ourbranch/lookups';
import { getPrimaryFarmMortgage } from '@ourbranch/farm-utils';

// this is why we need to replace the dashes with slashes, but preferably, don't use new Date() on the frontend
// https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off?page=1&tab=scoredesc#tab-top

// Billing/payment-method rules were relocated here from policy-settings.validation.js when the
// billing form moved to the Payment & Price tab (#32450). Injected data (policyDetails,
// billingDetails, primaryMortgage) arrives via the Yup validation `context`, matching how this
// schema already reads isTeamLeader / maxBillingHoldDate / sevenDaysBeforeEndDate.
export const validationSchema = Yup.object()
  .shape({
    billingHoldUntil: Yup.string()
      .test(
        'incorrect',
        'Billing hold cannot be on this date. See calendar picker for available dates.',
        function test(value) {
          if (this.options.context.isTeamLeader) {
            return true;
          }
          // The date cap governs holds the staffer is setting or moving, not one already on the
          // policy. The renewal creator places a system hold to the term effective date on the first
          // renewal of a migrated policy (#35280) — usually well past the cap — and
          // that must not block saving unrelated fields on this tab.
          if (value === this.options.context.savedBillingHoldUntil) {
            return true;
          }
          // payment-tab.jsx seeds the cap as a Date; the "member paid early" checkbox swaps in a
          // 'yyyy-MM-dd' string. Accept both.
          const { maxBillingHoldDate } = this.options.context;
          const maxDate =
            maxBillingHoldDate instanceof Date ? maxBillingHoldDate : new Date(maxBillingHoldDate.replace(/-/g, '/'));
          if (isAfter(new Date(value.replace(/-/g, '/')), maxDate)) {
            return this.createError({
              message: `Billing hold cannot be after ${format(maxDate, 'yyyy-MM-dd')}. See calendar picker for available dates.`
            });
          }
          return true;
        }
      )
      .test('invalid', 'Billing holds cannot be placed within 7 days of the policy end date', function test(value) {
        return isBefore(new Date(value), this.options.context.sevenDaysBeforeEndDate);
      })
      .nullable(),
    // Place-on-Hold brand-optional fields (#33568). Both are gated by brand config injected via context;
    // Branch (requireReason=false, holdCancellationsEnabled=false) is unaffected.
    billingHoldReason: Yup.string()
      .nullable()
      .test('required-for-brand', 'Hold Reason is required.', function test(value) {
        const { billingHoldConfig, placingBillingHold } = this.options.context;
        // Mutual brands require a reason when a hold is actually being placed.
        return !(billingHoldConfig?.requireReason && placingBillingHold && !value?.trim());
      }),
    billingHoldBlocksCancellations: Yup.boolean()
      .nullable()
      .test('brand-enabled', 'Holding cancellations is not enabled for this brand.', function test(value) {
        const { billingHoldConfig } = this.options.context;
        return !(value && !billingHoldConfig?.holdCancellationsEnabled);
      }),
    paymentType: Yup.string().test('valid', 'Invalid payment type for that payment method', function test(value) {
      if (value === null) {
        return true;
      }

      const { policyDetails } = this.options.context;
      if (
        (value === paymentType.Escrow && this.parent.paymentMethod !== paymentMethod.Escrow) ||
        (this.parent.paymentMethod === paymentMethod.Escrow && value !== paymentType.Escrow)
      ) {
        const paymentMethodLabel = getValue(
          policyDetails.home ? 'homeownersPaymentMethod' : 'condoPaymentMethod',
          this.parent.paymentMethod
        );
        const paymentTypeLabel = getValue('paymentType', value);
        return this.createError({
          path: this.path,
          message: `${paymentTypeLabel} payment type is not valid for ${paymentMethodLabel} payment method`
        });
      }

      return true;
    }),
    renewalPaymentMethod: Yup.string()
      .test('hasPaymentMethodReadyToCharge', 'There is no payment for this payment type', function test(value) {
        const { policyDetails, billingDetails } = this.options.context;
        const currentPaymentMethod = this.parent.paymentMethod;
        if (!value || value === currentPaymentMethod) {
          return true;
        }

        // Farm mortgages live per-structure on building.mortgageDetails (#32526); the legacy flat
        // farm.mortgageDetails is cleared on save, so resolve the farm primary from the buildings.
        const primaryMortgage =
          policyDetails?.home?.mortgageDetails?.find((md) => md.primary) ||
          policyDetails?.condo?.mortgageDetails?.find((md) => md.primary) ||
          getPrimaryFarmMortgage(policyDetails);

        if (value === paymentMethod.Escrow && !primaryMortgage) {
          return this.createError({
            path: this.path,
            message: 'Need primary mortgage on policy to be able to have a renewal payment method of Mortgage.'
          });
        }
        if (value === paymentMethod.CreditCard) {
          const hasCreditCard = billingDetails.allPaymentMethods.find(
            ({ id, brand }) => id?.startsWith('card') || (id?.startsWith('pm') && !!brand)
          );
          const hasStripeCustomerId = this.parent.stripeCustomerId?.startsWith('cus');
          if (!hasStripeCustomerId || !hasCreditCard) {
            return this.createError({
              path: this.path,
              message: 'Need credit card on policy to have renewal payment method of Credit Card'
            });
          }
        }
        if (value === paymentMethod.ACH) {
          const hasStripeCustomerId = this.parent.stripeCustomerId?.startsWith('cus');
          // Stripe returns current bank accounts as pm_ PaymentMethods with no routingNumber; only
          // legacy sources use ba_ ids. Billing tags both with type us_bank_account, so accept that too.
          const hasBankAccount = billingDetails.allPaymentMethods.some(
            ({ id, type, routingNumber }) => id?.startsWith('ba') || routingNumber || type === 'us_bank_account'
          );

          if (!hasStripeCustomerId || !hasBankAccount) {
            return this.createError({
              path: this.path,
              message: 'Need bank account on policy to have renewal payment method of ACH'
            });
          }
        }
        return true;
      })
      .nullable(),
    renewalPaymentType: Yup.string().test(
      'valid',
      'Invalid payment type for the renewal payment method',
      function test(value) {
        if (value == null || this.parent.renewalPaymentMethod == null) {
          return true;
        }

        if (
          (value === paymentType.Escrow && this.parent.renewalPaymentMethod !== paymentMethod.Escrow) ||
          (this.parent.renewalPaymentMethod === paymentMethod.Escrow && value !== paymentType.Escrow)
        ) {
          return false;
        }

        return true;
      }
    ),
    defaultEscrowAccount: Yup.object().when('paymentMethod', {
      is: paymentMethod.Escrow,
      then: Yup.object().shape({
        mortgageHolderName: Yup.string().test(
          'requireIfNotCancelling',
          'Escrow mortgage holder name is required',
          function test(value) {
            // The form value is seeded once at mount; the policy's own mortgage is the source of
            // truth, so a mortgage on the policy satisfies escrow billing even when the seeded form
            // value is empty. primaryMortgage is the same value the tab backfills on submit.
            const { primaryMortgage, cancel } = this.options.context;
            return !!value || cancel || !!primaryMortgage?.mortgageHolderName;
          }
        )
      }),
      otherwise: Yup.object().nullable()
    }),
    defaultBankAccount: Yup.object().when('paymentMethod', {
      is: paymentMethod.ACH,
      then: Yup.object()
        .shape({
          id: Yup.string().required('Bank account information is required')
        })
        .typeError('Bank account information is required'),
      otherwise: Yup.object().nullable()
    }),
    defaultCreditCard: Yup.object().when('paymentMethod', {
      is: paymentMethod.CreditCard,
      then: Yup.object()
        .shape({
          id: Yup.string().required('Credit card information is required')
        })
        .typeError('Credit card information is required'),
      otherwise: Yup.object().nullable()
    })
  })
  .nullable()
  .default(null);
